"use client"

import { useMemo } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { useCountry } from "@/components/layout/country-provider"
import type { NavbarItem } from "@/components/ui/navbar"
import { isAllowedInCountry } from "@/lib/country-gates"
import { useLocalePath } from "@/lib/use-localized-navigation"

/**
 * The membership-first navbar's items, made from today's
 * (`useLocalizedNavigation`): one mega menu, on Check-ups. How it works is a
 * plain link (17.09), and the two pages its menu listed that the redesign
 * keeps, health risks and biomarkers, lead the Check-ups menu instead. Every
 * other item is today's, unchanged.
 *
 * PageShell hands these to `MembershipFirstNavbar` and today's items to
 * `Navbar`. After launch this list becomes the nav hook's own.
 */
export function useMembershipFirstNavItems(items: NavbarItem[]): NavbarItem[] {
  const t = useTranslations("nav")
  const { code: country } = useCountry()
  const localePath = useLocalePath()
  return useMemo(
    () =>
      items.map((item) => {
        if (item.megaMenuType === "howItWorks") {
          return { label: item.label, href: item.href }
        }
        // Pricing's slot goes to Scans, a plain link to the MRI page, whose
        // add-ons list the other scans (DEXA, the calcium score's CT). "Scans"
        // rather than "MRI" because it covers all three, and no dropdown
        // (Eugene, 20.10). Prices stay reachable from Check-ups' Add-ons link
        // on phones (mobile-menu-membership-first).
        if (item.href === localePath("/pricing")) {
          return {
            label: t("scans"),
            href: localePath("/tests/full-body-mri"),
          }
        }
        if (item.megaMenuType === "whatWeOffer") {
          return {
            ...item,
            children: [
              { label: t("healthRisks"), href: localePath("/what-we-detect") },
              ...(isAllowedInCountry("page:biomarkers", country)
                ? [
                    {
                      label: t("biomarkersWeCheck"),
                      href: localePath("/biomarkers"),
                    },
                  ]
                : []),
              ...(item.children ?? []),
            ],
          }
        }
        return item
      }),
    [items, t, localePath, country]
  )
}
